import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/**
 * IndexedDB: fila durável de mutações, cache de leitura e blobs de foto
 * pendentes. NUNCA é fonte de verdade de dado oficial — é o Postgres.
 *
 * localStorage fica restrito a preferências pequenas (tenant ativo, tema).
 * Ver docs/PERSISTENCIA-OFFLINE.md.
 */

export type MutationStatus = 'pending' | 'inflight' | 'failed' | 'conflict';

export interface OutboxRecord {
  mutation_id: string;
  organization_id: string;
  kind: string;
  entity_type: string;
  entity_id: string | null;
  payload: Record<string, unknown>;
  created_at: number;
  attempts: number;
  next_attempt_at: number;
  last_error: string | null;
  status: MutationStatus;
  depends_on: string | null;
  label: string;
}

export interface BlobRecord {
  blob_id: string;
  mutation_id: string;
  organization_id: string;
  blob: Blob;
  thumbnail: Blob | null;
  mime: string;
  bytes: number;
  width: number;
  height: number;
  sha256: string;
}

export interface CacheRecord {
  key: string;
  entity_type: string;
  id: string;
  organization_id: string;
  data: unknown;
  fetched_at: number;
  server_version: number | null;
}

interface Nr12DB extends DBSchema {
  outbox: {
    key: string;
    value: OutboxRecord;
    indexes: { by_status: MutationStatus; by_created_at: number; by_org: string };
  };
  blobs: {
    key: string;
    value: BlobRecord;
    indexes: { by_mutation: string };
  };
  cache: {
    key: string;
    value: CacheRecord;
    indexes: { by_entity: string; by_org: string };
  };
  meta: { key: string; value: unknown };
}

let dbPromise: Promise<IDBPDatabase<Nr12DB>> | null = null;

export function getDB(): Promise<IDBPDatabase<Nr12DB>> {
  if (!dbPromise) {
    dbPromise = openDB<Nr12DB>('nr12', 1, {
      upgrade(db) {
        const outbox = db.createObjectStore('outbox', { keyPath: 'mutation_id' });
        outbox.createIndex('by_status', 'status');
        outbox.createIndex('by_created_at', 'created_at');
        outbox.createIndex('by_org', 'organization_id');

        const blobs = db.createObjectStore('blobs', { keyPath: 'blob_id' });
        blobs.createIndex('by_mutation', 'mutation_id');

        const cache = db.createObjectStore('cache', { keyPath: 'key' });
        cache.createIndex('by_entity', 'entity_type');
        cache.createIndex('by_org', 'organization_id');

        db.createObjectStore('meta');
      },
    });
  }
  return dbPromise;
}

export function newMutationId(): string {
  return crypto.randomUUID();
}

/** Enfileira ANTES de qualquer tentativa de rede. Durável primeiro. */
/**
 * Carimbo de entrada ESTRITAMENTE crescente.
 *
 * `Date.now()` tem resolução de milissegundo, e duas mutações enfileiradas no
 * mesmo milissegundo ficam empatadas no índice `by_created_at`. Empate no
 * IndexedDB é resolvido pela chave primária — que aqui é um UUID, ou seja,
 * ordem sorteada. Foi assim que "iniciar a inspeção" apareceu DEPOIS da
 * primeira resposta dela numa execução de teste.
 *
 * A fila também tem `depends_on`, e é ele que garante a correção: a resposta
 * não sobe enquanto a inspeção estiver pendente. Mas depender só disso é
 * frágil — basta alguém esquecer de declarar a dependência numa chamada nova
 * para o defeito voltar, e ele só aparece em máquina rápida, de vez em quando.
 * A ordem da fila é a ordem em que as coisas aconteceram, e isso precisa valer
 * sempre.
 *
 * Entre ABAS diferentes o empate ainda é possível (cada aba tem o seu
 * contador); ali quem resolve continua sendo `depends_on`.
 */
let ultimoCarimbo = 0;

function carimbarEntrada(): number {
  const agora = Date.now();
  ultimoCarimbo = agora > ultimoCarimbo ? agora : ultimoCarimbo + 1;
  return ultimoCarimbo;
}

export async function enqueue(
  rec: Omit<OutboxRecord, 'created_at' | 'attempts' | 'next_attempt_at' | 'last_error' | 'status'>,
): Promise<OutboxRecord> {
  const db = await getDB();
  const full: OutboxRecord = {
    ...rec,
    created_at: carimbarEntrada(),
    attempts: 0,
    next_attempt_at: 0,
    last_error: null,
    status: 'pending',
  };
  await db.put('outbox', full);
  return full;
}

/**
 * Remove SOMENTE após ACK real do servidor.
 * Fechar o app, matar o navegador ou perder bateria no meio do envio não pode
 * perder o registro.
 */
export async function acknowledge(mutationId: string): Promise<void> {
  const db = await getDB();
  const tx = db.transaction(['outbox', 'blobs'], 'readwrite');
  await tx.objectStore('outbox').delete(mutationId);
  const blobStore = tx.objectStore('blobs');
  const keys = await blobStore.index('by_mutation').getAllKeys(mutationId);
  await Promise.all(keys.map((k) => blobStore.delete(k)));
  await tx.done;
}

export async function markFailure(
  mutationId: string,
  error: string,
  retryable: boolean,
): Promise<void> {
  const db = await getDB();
  const rec = await db.get('outbox', mutationId);
  if (!rec) return;
  rec.attempts += 1;
  rec.last_error = error;
  rec.status = retryable ? 'pending' : 'failed';
  rec.next_attempt_at = Date.now() + backoffMs(rec.attempts);
  await db.put('outbox', rec);
}

export async function markStatus(mutationId: string, status: MutationStatus): Promise<void> {
  const db = await getDB();
  const rec = await db.get('outbox', mutationId);
  if (!rec) return;
  rec.status = status;
  await db.put('outbox', rec);
}

/** 1s, 2s, 5s, 15s, 30s, 60s (teto), com jitter. */
export function backoffMs(attempt: number): number {
  const ladder = [1_000, 2_000, 5_000, 15_000, 30_000, 60_000];
  const base = ladder[Math.min(attempt - 1, ladder.length - 1)] ?? 60_000;
  return base + Math.floor(Math.random() * (base * 0.25));
}

export async function pendingMutations(organizationId?: string): Promise<OutboxRecord[]> {
  const db = await getDB();
  const all = await db.getAllFromIndex('outbox', 'by_created_at');
  return all.filter(
    (m) => (!organizationId || m.organization_id === organizationId) && m.status !== 'inflight',
  );
}

/**
 * TODAS as mutações que ainda estão na fila, INCLUSIVE as em trânsito.
 *
 * `pendingMutations` esconde as `inflight` de propósito: o contador da barra
 * não deve piscar a cada tentativa. Só que quem pergunta "esta resposta já
 * chegou ao servidor?" precisa enxergá-las — uma requisição pendurada (rede
 * que aceita a conexão e não responde) fica inflight por minutos, e some das
 * duas listas ao mesmo tempo. A tela então concluía "sincronizado", que é a
 * pior mentira possível nesta tela.
 */
export async function allMutations(organizationId?: string): Promise<OutboxRecord[]> {
  const db = await getDB();
  const all = await db.getAllFromIndex('outbox', 'by_created_at');
  return all.filter((m) => !organizationId || m.organization_id === organizationId);
}

export async function readyMutations(organizationId?: string): Promise<OutboxRecord[]> {
  const now = Date.now();
  const all = await pendingMutations(organizationId);
  return all.filter((m) => m.status === 'pending' && m.next_attempt_at <= now);
}

export async function countPending(organizationId?: string): Promise<number> {
  return (await pendingMutations(organizationId)).length;
}

// ---------------------------------------------------------------- blobs
export async function putBlob(rec: BlobRecord): Promise<void> {
  const db = await getDB();
  await db.put('blobs', rec);
}

export async function getBlobsFor(mutationId: string): Promise<BlobRecord[]> {
  const db = await getDB();
  return db.getAllFromIndex('blobs', 'by_mutation', mutationId);
}

// ---------------------------------------------------------------- cache
export const cacheKey = (entityType: string, id: string) => `${entityType}:${id}`;

export async function cachePut(rec: Omit<CacheRecord, 'key'>): Promise<void> {
  const db = await getDB();
  await db.put('cache', { ...rec, key: cacheKey(rec.entity_type, rec.id) });
}

/**
 * Grava vários registros em UMA transação.
 *
 * Chamar `cachePut` em laço abre uma transação por linha: com 50 itens de
 * lista isso vira 50 round-trips no IndexedDB e trava a renderização por
 * segundos. Aqui é uma transação só.
 */
export async function cachePutMany(recs: Omit<CacheRecord, 'key'>[]): Promise<void> {
  if (recs.length === 0) return;
  const db = await getDB();
  const tx = db.transaction('cache', 'readwrite');
  const store = tx.objectStore('cache');
  for (const rec of recs) {
    void store.put({ ...rec, key: cacheKey(rec.entity_type, rec.id) });
  }
  await tx.done;
}

export async function cacheGet(entityType: string, id: string): Promise<CacheRecord | undefined> {
  const db = await getDB();
  return db.get('cache', cacheKey(entityType, id));
}

export async function cacheList(entityType: string, organizationId: string): Promise<CacheRecord[]> {
  const db = await getDB();
  const rows = await db.getAllFromIndex('cache', 'by_entity', entityType);
  return rows.filter((r) => r.organization_id === organizationId);
}

/** Ao trocar de usuário, o cache de leitura sai; a FILA permanece. */
export async function clearCache(): Promise<void> {
  const db = await getDB();
  await db.clear('cache');
}
